import { useState, useContext, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { ProgressButtons, catcat as cc } from "cerulean";
import { modules, entities } from "byzantine";
import type Account from "byzantine/src/Account";
import { FREQUENCIES } from "byzantine/src/Recurrence";
import {
  MAX_MEMO_LENGTH,
  useMappedEntryDescription,
  getReadableTimeFromCutoff,
  type LimitsForDisplayType,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import filters from "byzantine/src/filters";
import { useLocalization } from "@fluent/react";

import {
  Alert,
  Button,
  ContentCard,
  TruncatedAccount,
  DisabledShim,
  DateInput,
  Radio,
  Select,
  TextInput,
  Toggle,
  Tooltip,
  ResponsiveFlex,
  Row,
} from "@narmi/design_system";
import HeaderWithSteps from "../../HeaderWithSteps";
import * as formAdapters from "../../../src/adapters";
import AccountContext from "../../contexts/AccountContext";
import ComboAccountSelector from "../../ComboAccountSelector";
import PreviewCard from "../../PreviewCard/PreviewCard";
import FrequencySelector from "../FrequencySelector";
import DeliverySpeedSection from "./DeliverySpeedSection";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { useSudoContext } from "../../SudoContext";
import { INITIAL_TOTAL_STEPS, INSTANT_TOTAL_STEPS } from "./RecipientScreen";
import {
  ACH_PAYMENT_ADDITIONAL_DETAILS_ROUTE,
  ACH_PAYMENT_REVIEW_ROUTE,
  ACH_PAYMENT_ROUTE,
} from "./ACHPaymentRoutes";
import SECModal from "./SECDialog";
import LimitsAndDisclosuresDialog from "./LimitsAndDisclosuresDialog";
import ACHCompanyMissingAlert from "./ACHCompanyMissingAlert";
import { RecipientDrawer, DRAWER_TYPES } from "../../RecipientDrawer";
import styles from "./PaymentScreen.module.scss";

export const TransactionTypeSection = ({
  codes,
}: {
  codes: API.ACHPayment.SECCode[];
}) => {
  const { l10n } = useLocalization();
  const [showSECDialog, setShowSECDialog] = useState(false);
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const { mappedOptions } = useMappedEntryDescription();

  const toggleSECDialog = () => {
    setShowSECDialog(!showSECDialog);
  };

  return (
    <>
      <ResponsiveFlex gapSize="m">
        <h4 className="fontSize--m nds-sans">
          {l10n.getString("add-recipient-drawer-additional-details-title")}
        </h4>
        <Select
          label={l10n.getString("ach-payment-transaction-title")}
          {...formAdapters.select(form, "secCode")}
        >
          {codes.map((code, index) => (
            <Select.Item key={`account_type_${index}`} value={code}>
              {code}
            </Select.Item>
          ))}
        </Select>
        <div>
          <Button
            label={l10n.getString(
              "ach-payment-learn-more-transaction-types-cta"
            )}
            kind="plain"
            size="xs"
            testId="ach-view-transaction-types"
            onClick={toggleSECDialog}
          />
        </div>
        <Select
          label={l10n.getString("label-entry-description")}
          {...formAdapters.select(form, "entryDesc")}
        >
          {mappedOptions.map(({ display, value }) => (
            <Select.Item key={value} value={value}>
              {display}
            </Select.Item>
          ))}
        </Select>
      </ResponsiveFlex>
      <SECModal
        isOpen={showSECDialog}
        handleClose={toggleSECDialog}
        codes={codes}
      />
    </>
  );
};

export const InternalNoteSection = () => {
  const { l10n } = useLocalization();
  const form = modules.achPayments.ACHPaymentForm.useForm();

  return (
    <ResponsiveFlex gapSize="m">
      <h4 className="fontSize--m nds-sans">
        {l10n.getString("ach-payment-internal-details")}
      </h4>
      <TextInput
        label={l10n.getString("label-note-optional")}
        maxLength={MAX_MEMO_LENGTH}
        {...formAdapters.input(form, "memo")}
      />
    </ResponsiveFlex>
  );
};

export const ACHArrivalEstimate = () => {
  const { l10n } = useLocalization();
  const estimate = modules.achPayments.useACHArrivalEstimate();
  const { send: fetchHolidays, hasData: hasHolidays } =
    entities.holidays.useFetchHolidays();

  // holidays are only needed to compute the standard ACH arrival estimate
  const needsHolidays = estimate?.kind === "standard";
  useEffect(() => {
    if (needsHolidays && !hasHolidays) {
      fetchHolidays();
    }
  }, [needsHolidays, fetchHolidays, hasHolidays]);

  if (!estimate) {
    return null;
  }

  // the date field and this copy is hidden for expedited speeds
  if (
    estimate.kind === "instant" ||
    estimate.kind === "sameDay" ||
    estimate.kind === "sameDayCutoffPassed"
  ) {
    return null;
  }

  let text: string;
  switch (estimate.kind) {
    case "dualApproval":
      text = l10n.getString("ach-arrival-estimate-dual-approval");
      break;
    default:
      text = l10n.getString("ach-arrival-estimate-standard", {
        arrivalDate: estimate.arrivalDate,
      });
  }

  return (
    <div
      className={cc([
        "fontColor--secondary fontSize--s",
        styles.arrivalEstimate,
      ])}
    >
      {text}
    </div>
  );
};

const PaymentDetailsSection = ({
  limits,
  isACHCompanyMissing,
}: {
  limits: LimitsForDisplayType;
  isACHCompanyMissing: boolean;
}) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext);
  const [showLimitsDialog, setShowLimitsDialog] = useState(false);
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const isDebitPayment = modules.achPayments.useIsDebitACHPayment();
  const isSingleExpediteEnabled =
    modules.achPayments.useIsSingleExpediteEnabled();
  const isInstantDeliverySelected =
    modules.achPayments.useIsInstantACHDelivery();
  const isExpeditedDeliverySelected =
    modules.achPayments.useIsExpeditedACHDelivery();
  const isInstantDelivery =
    isSingleExpediteEnabled && isInstantDeliverySelected;
  const isExpeditedDelivery =
    isSingleExpediteEnabled && isExpeditedDeliverySelected;
  const { setFieldValue, values } = form;
  const { recurringRule } = values;
  const institution = useInstitutionSettings();
  const minDate = modules.achPayments.getStartDate(institution.ach_next_cutoff);
  const cutoffTime = getReadableTimeFromCutoff(institution.ach_next_cutoff);

  // TODO: fix when we integrate fednow endpoint
  // for expedited payments (instant, same day), the date picker is hidden so pin startDate to today
  // for standard payments, we default to earliest valid date (so override it if user has previously selected a date before that)
  useEffect(() => {
    if (isExpeditedDelivery) {
      const today = modules.achPayments.getStartDate();
      if (values.startDate !== today) {
        setFieldValue("startDate", today);
      }
      return;
    }
    const startDate = DateTime.fromFormat(values.startDate, "MM/dd/yyyy");
    const earliest = DateTime.fromFormat(minDate, "MM/dd/yyyy");
    // reset to the earliest valid date if the stored date is invalid or falls before it
    if (!startDate.isValid || startDate < earliest) {
      setFieldValue("startDate", minDate);
    }
  }, [isExpeditedDelivery, minDate, values.startDate, setFieldValue]);

  const { onChange: startDateOnChange, ...restOfStartDateFieldProps } =
    formAdapters.fieldWithOnChange(form, "startDate");

  const handleStartDateChange = (value: string) => {
    const startDateTime = DateTime.fromFormat(value, "M/d/yyyy");
    const formattedStartDateTimeString = startDateTime.toFormat("MM/dd/yyyy");

    if (
      recurringRule?.untilDate &&
      startDateTime >
        DateTime.fromFormat(recurringRule?.untilDate, "MM/dd/yyyy")
    ) {
      const formattedUntilDate = startDateTime
        .plus({ years: 1 })
        .toFormat("MM/dd/yyyy");
      setFieldValue("recurringRule.untilDate", formattedUntilDate);
    }
    startDateOnChange(formattedStartDateTimeString);
  };

  const validAccounts = accounts.filter((account: Account) =>
    account.isValidAchTransferSource()
  );

  const toggleLimitsDialog = () => {
    setShowLimitsDialog(!showLimitsDialog);
  };

  const fromAccountProps = formAdapters.select(form, "fromAccount");
  const achCompanyFieldError = isACHCompanyMissing
    ? l10n.getString("ach-company-missing-field-error")
    : undefined;

  return (
    <>
      <ResponsiveFlex gapSize="m">
        <div className="alignChild--left--center">
          <h4 className="fontSize--m nds-sans">
            {l10n.getString("ach-payment-payment-details-title")}
          </h4>
          {!isInstantDelivery ? (
            <div className={styles.endRowContainer}>
              <Button
                label={l10n.getString("ach-payment-view-limits-cta")}
                kind="plain"
                size="xs"
                testId="ach-view-limits"
                onClick={toggleLimitsDialog}
              />
            </div>
          ) : null}
        </div>
        <Alert
          kind="warn"
          isActive={validAccounts.length === 0}
          isDismissable={false}
        >
          <span>
            A deposit account with a balance greater than $0 is required to use
            this feature.
          </span>
        </Alert>
        <ComboAccountSelector
          label={
            isDebitPayment
              ? l10n.getString("single-ach-payment-to-account")
              : l10n.getString("single-ach-payment-from-account")
          }
          accounts={validAccounts}
          isDestination={true}
          showAddExternalAccountLink={false}
          {...fromAccountProps}
          error={achCompanyFieldError || fromAccountProps.error}
          errorText={achCompanyFieldError || fromAccountProps.errorText}
        />
        <TextInput
          label={l10n.getString("label-amount")}
          {...formAdapters.amountInput(form, "amount")}
        />

        <TextInput
          label={l10n.getString("label-memo-optional")}
          maxLength={MAX_MEMO_LENGTH}
          {...formAdapters.input(form, "description")}
        />

        {isExpeditedDelivery ? null : (
          <div>
            {cutoffTime ? (
              <Row alignItems="center" justifyContent="end">
                <Row.Item shrink>
                  <span
                    className={cc([
                      "fontColor--secondary fontSize--s",
                      styles.dateInputCutoffLabel,
                    ])}
                  >
                    {l10n.getString("ach-payment-cutoff-time", { cutoffTime })}
                  </span>
                </Row.Item>
              </Row>
            ) : null}
            <DateInput
              minDate={minDate}
              dateFormat={"m/d/Y"}
              altInput={true}
              altFormat={"m/d/Y"}
              field="date"
              label={l10n.getString("label-date-once")}
              useIsoOnChange={false}
              onChange={handleStartDateChange}
              {...restOfStartDateFieldProps}
            />
          </div>
        )}
        <ACHArrivalEstimate />
      </ResponsiveFlex>
      <LimitsAndDisclosuresDialog
        isOpen={showLimitsDialog}
        handleClose={toggleLimitsDialog}
        limits={limits}
      />
    </>
  );
};

const RecipientCardSection = () => {
  const { l10n } = useLocalization();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const hasManageRecipientPermission =
    modules.achPayments.useManageRecipientPermission();
  const accountNumber = selectedRecipient?.ach_destination?.account_number;
  const bankName = selectedRecipient?.ach_destination?.institution_name ?? "";
  const lastFour = accountNumber
    ? accountNumber.substring(accountNumber.length - 4)
    : "";
  const { nickname } = selectedRecipient as API.Recipient;

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const PreviewCardActionButton = (
    <Button
      kind="plain"
      label={l10n.getString("button-edit")}
      testId="ach-edit-recipient-button"
      onClick={toggleDrawer}
    />
  );
  const PreviewCardIconContent = (
    <TruncatedAccount lastFour={lastFour} name={bankName} />
  );

  const getPreviewCardHeader = () => {
    let header = "";
    if (selectedRecipient) {
      header += selectedRecipient.name;
    }
    return header;
  };

  return (
    <>
      <ResponsiveFlex gapSize="m">
        <h4 className="fontSize--m nds-sans">
          {l10n.getString("ach-payment-recipient-title")}
        </h4>
        <PreviewCard
          header={getPreviewCardHeader()}
          subtleHeader={nickname ? ` (${selectedRecipient?.nickname})` : ""}
          iconName="narmi-icon-bank"
          iconContent={PreviewCardIconContent}
          {...(hasManageRecipientPermission && {
            actionButton: PreviewCardActionButton,
          })}
        />
      </ResponsiveFlex>
      <RecipientDrawer
        isOpen={isDrawerOpen}
        handleClose={toggleDrawer}
        recipientId={selectedRecipient?.id}
        fromACHFlow={true}
        drawerType={DRAWER_TYPES.edit}
      />
    </>
  );
};

const RepeatPaymentCard = ({ totalPayments }: { totalPayments: number }) => {
  const { l10n } = useLocalization();
  const isDebitPayment = modules.achPayments.useIsDebitACHPayment();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const { values } = form;
  const { recurringRule } = values;
  const userToggledOnOption = recurringRule?.ends === "on";
  const userToggledAfterOption = recurringRule?.ends === "after";

  const { onChange: frequencyOnChange, ...restOfFrequencyFieldProps } =
    formAdapters.fieldWithOnChange(form, "recurringRule.frequency");

  const { onChange: countOnChange, ...restOfCountFieldProps } =
    formAdapters.fieldWithOnChange(form, "recurringRule.count");

  const { onChange: untilDateOnChange, ...restOfUntilDateFieldProps } =
    formAdapters.fieldWithOnChange(form, "recurringRule.untilDate");

  const { onChange: endsOnChange, ...restOfEndFieldProps } =
    formAdapters.fieldWithOnChange(form, "recurringRule.ends");

  const handleFrequencyChange = (value: string) => {
    frequencyOnChange(value);
  };

  const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const numericalValue = Number(value);

    if (!numericalValue) {
      countOnChange("");
    }
    if (numericalValue >= 100) {
      countOnChange(value.slice(0, 2));
    }
    countOnChange(value);
  };

  const handleUntilDateChange = (value: string) => {
    const untilDateTime = DateTime.fromFormat(value, "M/d/yyyy");
    const formattedUntilDateTimeString = untilDateTime.toFormat("MM/dd/yyyy");
    untilDateOnChange(formattedUntilDateTimeString);
  };

  const handleEndChange = (value: string) => {
    endsOnChange(value);
  };

  const calculateTotalAmount = () => {
    const totalAmountInCents = (totalPayments * values.amount) as Cents;
    return filters.currency(totalAmountInCents);
  };

  const totalPaymentsLabel = isDebitPayment
    ? l10n.getString(
        "ach-payment-collecting-total-payments",
        { totalPayments },
        `Collecting ${totalPayments} payments total`
      )
    : `${totalPayments} ${l10n.getString("ach-payment-total-payments")}`;

  return (
    <ContentCard kind="bordered">
      <div className={styles.contentCardContainer}>
        <ResponsiveFlex gapSize="m">
          <FrequencySelector
            label={l10n.getString("label-frequency")}
            excludedFrequencies={[FREQUENCIES.ONCE]}
            onChange={handleFrequencyChange}
            {...restOfFrequencyFieldProps}
          />
        </ResponsiveFlex>

        <div>
          <ResponsiveFlex gapSize="m">
            <DisabledShim isDisabled={!recurringRule?.frequency}>
              <h4 className="fontSize--m nds-sans margin--top--xl">Ends</h4>
            </DisabledShim>
            <Radio
              name="recurringRule.ends"
              isDisabled={!recurringRule?.frequency}
              checked={recurringRule?.ends === "never"}
              onCheck={handleEndChange}
              {...restOfEndFieldProps}
              value="never"
            >
              {l10n.getString("ach-payment-never")}
            </Radio>
            <div className={cc(styles.radioInput, styles.dateInput)}>
              <Radio
                name="recurringRule.ends"
                isDisabled={!recurringRule?.frequency}
                checked={recurringRule?.ends === "on"}
                onCheck={handleEndChange}
                {...restOfEndFieldProps}
                value="on"
              >
                {l10n.getString("ach-payment-on")}{" "}
                <DateInput
                  disabled={!userToggledOnOption}
                  minDate={values.startDate}
                  dateFormat={"m/d/Y"}
                  altInput={true}
                  altFormat={"m/d/Y"}
                  field="date"
                  useIsoOnChange={false}
                  onChange={handleUntilDateChange}
                  {...restOfUntilDateFieldProps}
                />
              </Radio>
            </div>
            <div className={cc(styles.radioInput, styles.countInput)}>
              <Radio
                name="recurringRule.ends"
                isDisabled={!recurringRule?.frequency}
                checked={recurringRule?.ends === "after"}
                onCheck={handleEndChange}
                {...restOfEndFieldProps}
                value="after"
              >
                {l10n.getString("ach-payment-after")}
                <TextInput
                  disabled={!userToggledAfterOption}
                  type="text"
                  onChange={handleCountChange}
                  {...restOfCountFieldProps}
                />
                {l10n.getString("ach-payment-payments")}
              </Radio>
            </div>
          </ResponsiveFlex>
          {(userToggledAfterOption && recurringRule.count) ||
          userToggledOnOption ? (
            <div className={styles.totalCountSection}>
              <Row>
                <Row.Item>{`${totalPaymentsLabel}:`}</Row.Item>
                <Row.Item shrink>{calculateTotalAmount()}</Row.Item>
              </Row>
            </div>
          ) : null}
        </div>
      </div>
    </ContentCard>
  );
};

const PaymentScreen = ({ limits }: { limits: LimitsForDisplayType }) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { establishSudo, onSetInitialSteps } = useSudoContext();
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const { onContinue } = modules.achPayments.usePaymentDetails();
  const { calculateTotalPayments } =
    modules.achPayments.useCalculateTotalPayments();
  const isDebitPayment = modules.achPayments.useIsDebitACHPayment();
  const isSingleExpediteEnabled =
    modules.achPayments.useIsSingleExpediteEnabled();
  const isInstantDeliverySelected =
    modules.achPayments.useIsInstantACHDelivery();
  const isInstantDelivery =
    isSingleExpediteEnabled && isInstantDeliverySelected;
  const isSingleExpediteAvailable =
    modules.achPayments.useIsSingleExpediteAvailable();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const { values } = form;
  const { recurringRule } = values;
  const isExpeditedDeliverySelected =
    modules.achPayments.useIsExpeditedACHDelivery();
  const isExpeditedDelivery =
    isSingleExpediteEnabled && isExpeditedDeliverySelected;
  const [showRepeatPayment, setShowRepeatPayment] = useState(
    values.recurringRule?.isVisible
  );
  const [totalPayments, setTotalPayments] = useState(0);
  const { isACHCompanyMissing, loading: isACHCompanyLoading } =
    entities.achCompanies.useACHCompanyForAccount(values.fromAccount);
  // instant delivery is sent over FedNow, whose payload carries no ACH company,
  // so those payments are fine from an account that has none
  const requiresACHCompany = !isInstantDelivery;
  const blockForMissingACHCompany = requiresACHCompany && isACHCompanyMissing;
  const isACHCompanyPending = requiresACHCompany && isACHCompanyLoading;
  const headerText = isDebitPayment
    ? l10n.getString("single-ach-payment-collect-title")
    : l10n.getString("single-ach-payment-send-title");

  if (!selectedRecipient) {
    return <Navigate to={`/transfer/${ACH_PAYMENT_ROUTE}`} replace />;
  }

  const { onChange, ...restOfFieldProps } = formAdapters.fieldWithOnChange(
    form,
    "recurringRule.isVisible"
  );

  const toggleRepeatPayment = () => {
    setShowRepeatPayment(!showRepeatPayment);
  };

  const handleOnChange = (isVisible: boolean) => {
    toggleRepeatPayment();
    if (isVisible && !recurringRule?.ends) {
      form.setFieldValue("recurringRule.ends", "never");
    }
    onChange(isVisible);
  };

  useEffect(() => {
    const payments = calculateTotalPayments();
    setTotalPayments(payments);
  }, [recurringRule, values.startDate]);

  // the delivery speed is chosen on this step, so keep the flow's total step
  // count in sync (instant delivery skips the additional details step)
  useEffect(() => {
    onSetInitialSteps(
      isInstantDelivery ? INSTANT_TOTAL_STEPS : INITIAL_TOTAL_STEPS
    );
  }, [isInstantDelivery, onSetInitialSteps]);

  // recurring payments are not supported for expedited delivery (instant or
  // same day), so clear any recurrence the user may have selected before
  // switching to an expedited speed
  useEffect(() => {
    if (isExpeditedDelivery && values.recurringRule?.isVisible) {
      setShowRepeatPayment(false);
      form.setFieldValue("recurringRule.isVisible", false);
    }
  }, [isExpeditedDelivery]);

  const handleClickNext = () => {
    // the selected account has no ACH company, so the payment cannot be
    // originated — keep the user on this step to pick another account.
    // while the lookup is still pending we don't yet know, so don't advance
    if (blockForMissingACHCompany || isACHCompanyPending) return;

    const handleNext = () => {
      // instant delivery skips the Additional Details step
      const nextRoute = isInstantDelivery
        ? ACH_PAYMENT_REVIEW_ROUTE
        : ACH_PAYMENT_ADDITIONAL_DETAILS_ROUTE;
      navigate(`/transfer/${ACH_PAYMENT_ROUTE}/${nextRoute}`);
    };
    const handleTrySudo = () => {
      establishSudo(
        `/transfer/${ACH_PAYMENT_ROUTE}/${ACH_PAYMENT_REVIEW_ROUTE}`
      );
    };

    onContinue(handleNext, handleTrySudo);
  };

  return (
    <>
      <ResponsiveFlex gapSize="xl">
        <HeaderWithSteps
          headerText={headerText}
          icon={
            !isSingleExpediteAvailable ? (
              <Tooltip
                text={l10n.getString("ach-payment-payment-title-tooltip")}
              >
                <i className={`narmi-icon-info ${styles.headerInfoIcon}`} />
              </Tooltip>
            ) : undefined
          }
        />

        <ACHCompanyMissingAlert
          isActive={blockForMissingACHCompany}
          shouldScrollIntoView
        />

        <RecipientCardSection />

        {isSingleExpediteEnabled ? <DeliverySpeedSection /> : null}

        <PaymentDetailsSection
          limits={limits}
          isACHCompanyMissing={blockForMissingACHCompany}
        />

        {/* recurring payments are not supported for expedited delivery (instant or same day) */}
        {isExpeditedDelivery ? null : (
          <>
            <Row gapSize="s">
              <Toggle
                onChange={handleOnChange}
                labelledBy="repeat-payment-label"
                isActive={restOfFieldProps.value}
                {...restOfFieldProps}
              />
              <label id="repeat-payment-label" className="fontWeight--bold">
                {l10n.getString("ach-payment-repeat")}
              </label>
            </Row>

            {showRepeatPayment ? (
              <RepeatPaymentCard totalPayments={totalPayments} />
            ) : null}
          </>
        )}

        {/* the internal note (memo) normally lives on the additional details step
        when instant delivery is selected, we collect the note here on the payment step instead */}
        {isInstantDelivery ? <InternalNoteSection /> : null}

        <ProgressButtons
          backLabel={l10n.getString("button-back")}
          nextLabel={l10n.getString("button-next")}
          onBack={() => navigate(`/transfer/${ACH_PAYMENT_ROUTE}`)}
          onNext={handleClickNext}
          isNextLoading={isACHCompanyPending}
        />
      </ResponsiveFlex>
    </>
  );
};

export default PaymentScreen;
